import type { SoundfontSource } from "@midi-fantom-utils/player-core";
import { isSoundfontSource } from "@midi-fantom-utils/player-core";

declare const process: {
  env: {
    SOUNDFONT_URL?: string;
    SOUNDFONTS_URL?: string;
    APP_VERSION?: string;
    IS_DEV?: string;
  };
};

const OLD_STORAGE_KEY = "midi-fantom-utils/soundfont-source";
const STORAGE_KEY = "midi-fantom-utils/settings";

/**
 * Версия сборки, зашитая на этапе webpack (DefinePlugin) из
 * `process.env.APP_VERSION` CI — git describe + pipeline id. Для
 * локальной сборки без env — `"dev"`. Используется диагностикой и
 * `build-info.json` (см. webpack.config.cjs → BuildInfoPlugin).
 */
export const APP_VERSION = process.env.APP_VERSION || "dev";

/**
 * Признак dev-сборки, зашитый на этапе webpack (DefinePlugin) как
 * `process.env.IS_DEV = JSON.stringify(!isProd)` (dev = `webpack serve
 * --mode development`, prod = `webpack --mode production`). Используется
 * для визуального отличия плашки версии в dev от prod. В jsdom-тестах
 * DefinePlugin не работает — fallback `false` (ведём себя как prod).
 */
export const IS_DEV = process.env.IS_DEV === "true";

/**
 * Базовый URL внешнего статического каталога SoundFont
 * (https://soundfonts.nuware.ru — CORS `*`, immutable-кэш). От него
 * строятся `manifest.json` и ссылки на `.sf2`-файлы. Переопределяется
 * client-env `SOUNDFONTS_URL` (webpack.DefinePlugin), как `SOUNDFONT_URL`.
 */
export const DEFAULT_SOUNDFONTS_URL = "https://soundfonts.nuware.ru";

const readEnvCatalogUrl = (): string | undefined => {
  const value = process.env.SOUNDFONTS_URL;
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

export const soundfontsBaseUrl = (): string =>
  readEnvCatalogUrl() ?? DEFAULT_SOUNDFONTS_URL;

/** URL manifest.json каталога SoundFont. */
export const SOUNDFONTS_MANIFEST_URL = `${soundfontsBaseUrl()}/manifest.json`;

/** URL `.sf2`-файла из каталога. */
export const soundfontCatalogUrl = (file: string): string =>
  `${soundfontsBaseUrl()}/${file}`;

/**
 * Дефолтный URL SF2. Экспортируется, чтобы любой fallback (первый mount
 * без сохранённых настроек, клик по radio «Ссылка (CDN)» с пустым
 * инпутом) использовал один и тот же источник.
 */
export const DEFAULT_SOUNDFONT_URL = `${DEFAULT_SOUNDFONTS_URL}/general-user-gs-v1.471.sf2`;

const readEnvUrl = (): string | undefined => {
  const value = process.env.SOUNDFONT_URL;
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

const defaultSource = (): SoundfontSource => ({
  kind: "url",
  url: readEnvUrl() ?? DEFAULT_SOUNDFONT_URL,
});

/** Цвета сетки/клавиш piano roll (см. PianoRoll.getColors). */
export type PianoRollColors = {
  gridStrong: string;
  gridMed: string;
  gridFaint: string;
  gridSub: string;
  keyWhite: string;
  keyBlack: string;
  keyBorder: string;
  keyLabel: string;
};

/**
 * Дефолтные цвета piano roll — соответствуют CSS-переменным из
 * `app/src/styles/app.css`. Объект намеренно создаётся заново на каждый
 * вызов, чтобы не шарить ссылку (React state / persist должны получать
 * свежий объект).
 */
export const defaultPianoRollColors = (): PianoRollColors => ({
  gridStrong: "rgba(255,255,255,0.4)",
  gridMed: "rgba(255,255,255,0.18)",
  gridFaint: "rgba(255,255,255,0.1)",
  gridSub: "rgba(255,255,255,0.06)",
  keyWhite: "rgba(255,255,255,0.05)",
  keyBlack: "rgba(0,0,0,0.25)",
  keyBorder: "rgba(255,255,255,0.15)",
  keyLabel: "rgba(255,255,255,0.55)",
});

/** Высота piano roll по умолчанию (px). */
export const DEFAULT_PIANO_ROLL_HEIGHT = 260;

export type PianoRollAppearance = {
  colors: PianoRollColors;
  /** Высота piano roll по умолчанию, px. */
  height: number;
};

export type Settings = {
  soundfont: SoundfontSource;
  appearance: { pianoRoll: PianoRollAppearance };
};

const defaultAppearance = (): Settings["appearance"] => ({
  pianoRoll: {
    colors: defaultPianoRollColors(),
    height: DEFAULT_PIANO_ROLL_HEIGHT,
  },
});

/** Ключи полей цвета piano roll — единый источник для валидации. */
const PIANO_ROLL_COLOR_KEYS: ReadonlyArray<keyof PianoRollColors> = [
  "gridStrong",
  "gridMed",
  "gridFaint",
  "gridSub",
  "keyWhite",
  "keyBlack",
  "keyBorder",
  "keyLabel",
];

const isAppearance = (value: unknown): value is Settings["appearance"] => {
  if (!value || typeof value !== "object") return false;
  const v = value as { pianoRoll?: unknown };
  if (!v.pianoRoll || typeof v.pianoRoll !== "object") return false;
  const pr = v.pianoRoll as { colors?: unknown; height?: unknown };
  if (typeof pr.colors !== "object" || pr.colors === null) return false;
  if (typeof pr.height !== "number" || !Number.isFinite(pr.height)) return false;
  // Все 8 полей цвета должны присутствовать и быть строками — иначе
  // malformed localStorage пройдёт валидацию и упадёт в cssColorToHex.
  for (const key of PIANO_ROLL_COLOR_KEYS) {
    const c = (pr.colors as Record<string, unknown>)[key];
    if (typeof c !== "string") return false;
  }
  return true;
};

export const DEFAULT_SETTINGS: Settings = {
  soundfont: defaultSource(),
  appearance: defaultAppearance(),
};

const isSettings = (value: unknown): value is Settings => {
  if (!value || typeof value !== "object") return false;
  const v = value as { soundfont?: unknown };
  // Gate только по soundfont. Отсутствующий/битый `appearance` (в т.ч.
  // старые объекты до ввода настроек внешнего вида) не должен ронять весь
  // объект — `withAppearance` подставит дефолты без потери soundfont.
  return isSoundfontSource(v.soundfont);
};

/**
 * `kind: "file"` содержит blob:-URL, который становится мёртвым после
 * reload. Сохранять его в localStorage бесполезно (плеер не сможет
 * загрузить звук) и потенциально опасно (cross-tab утечка ссылки).
 * Поэтому при persist заменяем на дефолтный URL. `appearance` проходит
 * сквозь невредимым.
 */
const persistable = (s: Settings): Settings => {
  if (s.soundfont.kind === "file") {
    return { soundfont: defaultSource(), appearance: s.appearance };
  }
  return s;
};

/**
 * Гарантирует, что у настроек есть заполненный `appearance` (в т.ч. для
 * объектов из localStorage, которые прошли `isSettings` без него).
 */
const withAppearance = (s: {
  soundfont: SoundfontSource;
  appearance?: unknown;
}): Settings => {
  if (isAppearance(s.appearance)) {
    return { soundfont: s.soundfont, appearance: s.appearance };
  }
  return { soundfont: s.soundfont, appearance: defaultAppearance() };
};

const tryRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

export const loadSettings = (): Settings => {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as unknown;
      if (isSettings(parsed)) {
        // Старый ключ мигрировали в прошлой сессии, но если по какой-то
        // причине он остался — удаляем его сейчас, чтобы не накапливать
        // мёртвые ключи.
        tryRemove(OLD_STORAGE_KEY);
        return withAppearance(persistable(parsed));
      }
    }
    // Миграция со старого ключа — одноразово.
    const legacy = window.localStorage.getItem(OLD_STORAGE_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as unknown;
      if (isSoundfontSource(parsed)) {
        const migrated: Settings = withAppearance({ soundfont: parsed });
        try {
          window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(persistable(migrated))
          );
          window.localStorage.removeItem(OLD_STORAGE_KEY);
        } catch {
          // ignore — не критично
        }
        return migrated;
      }
    }
  } catch {
    // localStorage недоступен / повреждён — падаем на дефолт.
  }
  return DEFAULT_SETTINGS;
};

export const saveSettings = (settings: Settings): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(persistable(withAppearance(settings)))
    );
  } catch {
    // ignore — некритично.
  }
};
